import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MonitoringTag } from "@/hooks/useMonitoringTags";

interface TagFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (tag: Omit<MonitoringTag, 'id' | 'user_id' | 'created_at'>) => void;
  initialData?: MonitoringTag;
}

const CATEGORY_LABELS: Record<MonitoringTag['category'], string> = {
  lab: 'Exame Laboratorial',
  symptom: 'Sintoma',
  condition: 'Condição/Diagnóstico',
  medication: 'Medicação',
  vital_sign: 'Sinal Vital'
};

const DEFAULT_COLORS = [
  '#ef4444', '#f59e0b', '#10b981', '#3b82f6', 
  '#6366f1', '#8b5cf6', '#ec4899', '#14b8a6'
];

export function TagForm({ open, onOpenChange, onSubmit, initialData }: TagFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [category, setCategory] = useState<MonitoringTag['category']>(initialData?.category || 'lab');
  const [color, setColor] = useState(initialData?.color || '#3b82f6');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      category,
      color,
      is_suggested: false
    });
    onOpenChange(false);
    if (!initialData) {
      setName('');
      setCategory('lab');
      setColor('#3b82f6');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{initialData ? 'Editar Tag' : 'Nova Tag'}</DialogTitle>
            <DialogDescription>
              Crie uma tag personalizada para monitorar indicadores específicos
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Tag</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Glicemia"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Categoria</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as MonitoringTag['category'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2">
                {DEFAULT_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className="w-8 h-8 rounded-full border-2 transition hover:scale-110"
                    style={{
                      backgroundColor: c,
                      borderColor: color === c ? '#000' : 'transparent'
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {initialData ? 'Salvar' : 'Criar Tag'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
